
import { ChangeDetectorRef } from '@angular/core';

import { BulkAmountConfig, PriceConfig } from 'app/product-designer-config';
import Bowser from 'bowser';
import { of, throwError as observableThrowError } from 'rxjs';
import { mergeMap, delay, retryWhen } from 'rxjs/operators';

import { GlobalConstants } from './constants';

export interface Offset {
  left: number;
  top: number;
}

export namespace Utils
{
  // Is also defined at src/app/core/styling/_constants.scss for Sass
  export const MEDIUM_DEVICE_BREAKPOINT = 992;

  const LIMIT_FILTER_SIZES = [100, 200, 300, 500, 600, 700, 1000, 1500];

  export interface KeyValuePair<Key, Value> {
    key: Key;
    value: Value;
  }

  export function getRandomId(length: number = 8): string
  {
    if (length > 12) {
      // Wenn wir zu viele Stellen verwenden, stehen sonst hinten nur Nullen...
      return this.getRandomId(12) + this.getRandomId(length - 12);
    }
    let max = Math.pow(10, length) - 1;
    let min = Math.pow(10, length - 1);
    return (Math.floor(Math.random() * (max - min + 1)) + min).toString();
  }

  export async function sha256sum(str: string): Promise<string>
  {
    const data = new TextEncoder().encode(str);
    let hashBuffer;
    if (!crypto.subtle) {
      console.warn('SubtleCrypto is not available. Are you using https?');
      hashBuffer = crypto.getRandomValues(data);
    }
    else {
      hashBuffer = await crypto.subtle.digest('SHA-256', data);
    }
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
  }

  export function isDeprecatedBrowser(): boolean
  {
    // It is not guaranteed that all newer browsers will work without errors.
    // We may have to increase the minimum requirements from time to time.
    try {
      const bowser = Bowser.getParser(window.navigator.userAgent);
      const satisfied = bowser.satisfies({ // release year: 2020
        'chrome': '>80.0.0',
        'chromium': '>80.0.0',
        'firefox': '>72.0.0',
        'edge': '>79',
        'opera': '>66',
        'safari': '>14',
        'seamonkey': '>2.0.0',
        'vivaldi': '>2.10.0',
      });
      if (satisfied === undefined) {
        console.warn(`did not detect ${bowser.getBrowser()}`);
        return false;
      }
      return !satisfied;
    }
    catch (exception) {
      console.warn('failed to detect browser: ' + exception);
      return false;
    }
  }

  export function isIphone(): boolean
  {
    return !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
  }

  export function isSafari(): boolean
  {
    const agent = navigator.userAgent.toLowerCase();
    return /safari/.test(agent) && !/chrome/.test(agent);
  }

  export function isWebGLAvailable(): boolean
  {
    try {
      const canvas = document.createElement('canvas');
      return !!(
        canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      );
    } catch (e) {
      return false;
    }
  }

  export function hasHttpSchema(str: string): boolean
  {
    const re = RegExp('^https?://');
    return re.test(str);
  }

  export function isRelativeToDomain(str: string): boolean
  {
    return str.startsWith('/');
  }

  export function isObject(string: string): boolean
  {
    try {
      JSON.parse(string);
      return true;
    }
    catch (error) {
      return false;
    }
  }

  // Generator für Funktionen, die als Parameter an reduce übergeben werden.
  // Immer dann brauchbar, wenn man die Elemente in einem Array zu Gruppen
  // zusammenfassen will.
  //
  // Beispiel:
  // Reihe von Zahlen zu Gruppen zusammenfassen, so dass in einer Gruppe
  // die Summe nicht größer 42 ist:
  // nums.reduce(groupByCondition((group, num) => sum(group) + num <= 42), [])
  export function groupByCondition(
      condition: (group: Array<any>, element: any) => boolean
      ): (groups: Array<Array<any>>, element: any) => Array<Array<any>>
  {
    return (groups, element) => {
      if (groups.length < 1) {
        groups.push([]);
      }

      const currentGroup = groups[groups.length - 1];

      if (condition(currentGroup, element)) {
        currentGroup.push(element);
      } else {
        groups.push([element]);
      }

      return groups;
    };
  }

  // Array "flach" pressen: Array von Arrays zu einem Array mit einer
  // Dimension weniger verschmelzen:
  // [[a], [b,c], [d,e,f], [g]] -> [a,b,c,d,e,f,g]
  export function flattenArray(array: Array<Array<any>>): Array<any>
  {
    return Array.prototype.concat.apply([], array);
  }

  export function sum(summands: Array<number>): number
  {
    return summands.reduce((total, num) => total + num, 0);
  }

  export function repeat(count: number): Array<any>
  {
    return Array.apply(undefined, Array(count));
  }

  export function range(
    startInclusive: number, endExclusive: number): Array<number>
  {
    if (endExclusive < startInclusive) {
      return [];
    }

    let acc = repeat(endExclusive - startInclusive);

    for (let i = startInclusive; i < endExclusive; ++i) {
      acc[i - startInclusive] = i;
    }

    return acc;
  }

  export function padArray(arr: Array<any>, length: number, value: any):
      Array<any>
  {
    return arr.concat(repeat(Math.max(length - arr.length, 0)).map(_ => value));
  }

  export function zip(lhs: Array<any>, rhs: Array<any>): Array<Array<any>>
  {
    return lhs.map((el, idx) => [el, rhs[idx]]);
  }

  export function find(arr: Array<any>, predicate: (el: any) => boolean): any
  {
    return arr.filter(el => predicate(el))[0];
  }

  export function byKey(dict: Object): Array<any>
  {
    return Object.keys(dict);
  }

  export function byKeyValue(dict: Object): Array<KeyValuePair<any, any>>
  {
    return byKey(dict).map(k => ({key: k, value: dict[k]}));
  }

  export function byValue(dict: Object): Array<any>
  {
    return byKeyValue(dict).map(el => el.value);
  }

  export function intersperse(arr: Array<any>, element: any): Array<any>
  {
    return arr.reduce((acc, el, idx) => {
      acc.push(el);

      if (idx < arr.length - 1) {
        acc.push(element);
      }

      return acc;
    }, []);
  }

  export function isLargeDevice(): boolean
  {
    return window.innerWidth > MEDIUM_DEVICE_BREAKPOINT;
  }

  export function isLandscape(): boolean
  {
    return window.innerWidth > window.innerHeight;
  }

  export function sleep(milliseconds: number): Promise<void>
  {
    return new Promise<void>(resolve => setTimeout(resolve, milliseconds));
  }

  export function debounce(fn: () => any, delay: number): () => any
  {
    const state = {
      timer: undefined as any,
    };

    return () => {
      if (state.timer) {
        clearTimeout(state.timer);
      }

      state.timer = setTimeout(() => {
        fn();
        state.timer = undefined;
      }, delay);
    };
  }

  // example usage:
  // const resp = await this.http.request(req)
  //   .pipe(Utils.retryPipe())
  //   .toPromise()
  //   .catch((exc) => {(
  //     throw exc;
  //   });
  export function retryPipe(timeout?: number): any
  {
    const delayTime = timeout || GlobalConstants.HTTP_REQUEST_DEFAULT_TIMEOUT;

    return retryWhen((err) => {
      let retries = 0;

      return err.pipe(
        mergeMap((e) => {
          if (retries++ < GlobalConstants.HTTP_REQUEST_MAX_RETRIES &&
              !Utils.isClientError(e)) {
            return of(e).pipe(delay(delayTime));
          }
          return observableThrowError(e);
        })
      );
    });
  }

  export function isClientError(err: any): boolean
  {
    return (err.status && err.status >= 400 && err.status <= 500);
  }

  export function trackById(index: number, item: any): any
  {
    return item.id;
  }

  export function trackByLabel(index: number, item: any): any
  {
    return item.label;
  }

  export function eventPreventDefault(event: Event): void
  {
    event.preventDefault();
  }

  export function scrollToPosition(x: number = 0, y: number = 0): void
  {
    const frame = (<any>window).parentIFrame || window;
    if (frame.scrollToOffset) {
      frame.scrollToOffset(x, y);
    }
    else {
      frame.scrollTo(x, y);
    }
  }

  export function setBodyScrollable(isScrollable: boolean): void
  {
    if (isScrollable) {
      document.body.style.overflow = '';
      document.body.style.position = '';
    }
    else if (document.body.style.overflow !== 'hidden' &&
             document.body.style.position !== 'fixed') {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
    }
  }

  export function getTotalOffset(el: HTMLElement): Offset
  {
    const rect = el.getBoundingClientRect();

    return {
      left: rect.left,
      top: rect.top
    };
  }

  export function objectValuesToArray(obj: any): Array<any>
  {
    let arr = [];
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj.propertyIsEnumerable(key)) {
        arr.push(obj[key]);
      }
    }
    return arr;
  }

  export function getCurrentPriceConfig(
      priceConfig: PriceConfig, quantity: number): BulkAmountConfig|PriceConfig
  {
    if (priceConfig.bulkAmounts) {
      const sortedBulkAmounts =  priceConfig.bulkAmounts
        .sort((a, b) => b.minQuantity - a.minQuantity); // sorts downward

      for (const i in sortedBulkAmounts) {
        if (sortedBulkAmounts[i].minQuantity <= quantity) {
          return sortedBulkAmounts[i];
        }
      }
    }
    return priceConfig;
  }

  export function deepCopy(object): any
  {
    return JSON.parse(JSON.stringify(object));
  }

  export function splitText(
    text: string, splitIndices: Array<number>): Array<string>
  {
    return splitIndices
      .concat([text.length])
      .reduce(
        (state, idx) => ({
          chunks: state.chunks.concat([text.slice(state.offset, idx)]),
          offset: idx,
        }),
        { chunks: [], offset: 0 })
      .chunks;
  }

  export function optional(
    obj: {[key: string]: any}, path: Array<string>, defaultValue?: any): any
  {
    return path.reduce(
      (state, key) => (
        state.broken
          ? state
          : (!state.iter || Object.keys(state.iter).indexOf(key) === -1
             ? { broken: true, iter: defaultValue }
             : { broken: false, iter: state.iter[key] })
      ),
      { broken: false, iter: obj }
    ).iter;
  }

  export function tupleListToDictionary(
    tuples: Array<Array<any>>): {[key: string]: any}
  {
    return tuples.reduce((acc: {[key: string]: any}, el: Array<any>) => {
      acc[el[0]] = el[1];
      return acc;
    }, {});
  }

  export function isUnique(aspect: (el: any) => any = el => el):
    (el: any, idx: number, arr: Array<any>) => boolean
  {
    return (
      (el: any, idx: number, arr: Array<any>) =>
        idx === (arr.findIndex(other => aspect(other) === aspect(el)))
    );
  }

  export function rotationToOrigin(x: number, y: number): number
  {
    const r = Math.atan(y / x) * (180 / Math.PI);

    if (x >= 0 && y >= 0) {
      return r;
    } else if (x < 0 && y >= 0) {
      return (90 + r) + 90;
    } else if (x < 0 && y < 0) {
      return r + 180;
    } else {
      return (90 + r) + 270;
    }
  }

  export class ChangeDetection {
    private static _cdr: ChangeDetectorRef | undefined = undefined;
    public static isPaused: boolean = false;

    public static setHandle(cdr: ChangeDetectorRef): void {
      ChangeDetection._cdr = cdr;
    }

    public static pause(): void {
      if (! ChangeDetection._cdr) {
        return;
      }

      ChangeDetection._cdr.detach();
      ChangeDetection.isPaused = true;
    }

    public static resume(): void {
      if (! ChangeDetection._cdr) {
        return;
      }

      ChangeDetection._cdr.reattach();
      ChangeDetection.isPaused = false;

      ChangeDetection._cdr.detectChanges();
    }
  }

  export function steppedLimit(size: number): number
  {
    const limit = LIMIT_FILTER_SIZES.find(l => l > size);

    if (!limit) {
      return LIMIT_FILTER_SIZES.at(-1);
    }
    return limit;
  }

  export function url(link: string): string
  {
    return new URL(link, import.meta.url).toString();
  }
}
